/**
 * @module modules/pinpoint-try-job-dialog-sk
 * @description <h2><code>pinpoint-try-job-dialog-sk</code></h2>
 *
 * pinpoint-try-job-dialog-sk allows a user to trigger a Pinpoint A/B Try job.
 * While try jobs support more use cases, the use case for this dialog in perf
 * is for requesting additional traces on benchmark runs. We should avoid
 * building on top of this job dialog as we migrate towards the newer Pinpoint
 * frontend.
 *
 * pinpoint-try-job-dialog-sk is based off of bisect-dialog-sk.
 *
 * Request parameters:
 *   test path: the full string of trace name from chart-tooltip
 *   baseCommit: base commit
 *   endCommit: end commit
 *   story: the last sub_test name from test path
 * Once a validated user submits this dialog, there'll be an attempt to post an A/B job
 * request. If successful, the user will see a Pinpoint job created.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "PinpointTryJobDialogSk", {
    enumerable: true,
    get: function() {
        return PinpointTryJobDialogSk;
    }
});
require("../../../elements-sk/modules/select-sk");
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _live = require("lit/directives/live.js");
const _task = require("@lit/task");
const _jsonOrThrow = require("../../../infra-sk/modules/jsonOrThrow");
const _errorMessage = require("../../../elements-sk/modules/errorMessage");
require("@material/web/icon/icon.js");
require("../../../elements-sk/modules/icons/close-icon-sk");
require("../../../elements-sk/modules/spinner-sk");
const _aloginsk = require("../../../infra-sk/modules/alogin-sk/alogin-sk");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let PinpointTryJobDialogSk = class PinpointTryJobDialogSk extends _lit.LitElement {
    connectedCallback() {
        super.connectedCallback();
        (0, _aloginsk.LoggedIn)().then((status)=>{
            this.user = status.email;
        }).catch(_errorMessage.errorMessage);
    }
    createRenderRoot() {
        return this;
    }
    setTryJobInputParams(params) {
        this.testPath = params.testPath || '';
        this.baseCommit = params.baseCommit || '';
        this.endCommit = params.endCommit || '';
        this.story = params.story || '';
        this.configuration = params.configuration || '';
        this.benchmark = params.benchmark || '';
        this.jobUrl = '';
        // Reset task state
        this._request = null;
    }
    async open() {
        await this.updateComplete;
        this.dialog.showModal();
    }
    closeDialog() {
        this.dialog.close();
    }
    onDialogClick(event) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
            this.closeDialog();
        }
    }
    onSubmit(e) {
        e.preventDefault();
        const req = {
            name: 'Tracing Debug',
            base_git_hash: this.baseCommit,
            end_git_hash: this.endCommit,
            base_patch: '',
            experiment_patch: '',
            configuration: this.configuration || this.testPath.split('/')[1] || '',
            benchmark: this.benchmark || this.testPath.split('/')[2] || '',
            story: this.story,
            repository: 'chromium',
            bug_id: '',
            user: this.user,
            extra_test_args: `["--extra-chrome-categories","${this.traceArgs}"]`,
            use_new_pinpoint: false
        };
        req.name = `${req.name} on ${req.configuration}/${req.benchmark}/${req.story}`;
        this._request = req;
    }
    render() {
        return (0, _lit.html)`
      <dialog id='pinpoint-try-job-dialog' @click=${this.onDialogClick}>
        <h2>Debug Traces</h2>
        <button id="pinpoint-try-job-dialog-close" @click=${this.closeDialog}>
          <close-icon-sk></close-icon-sk>
        </button>
        <form id="pinpoint-try-job-form" @submit=${this.onSubmit}>
          <h3>Base Commit</h3>
          <input
            id="base-commit"
            type="text"
            .value=${(0, _live.live)(this.baseCommit)}
            @input=${(e)=>this.baseCommit = e.target.value}
          ></input>
          <h3>Experiment Commit</h3>
          <input
            id="exp-commit"
            type="text"
            .value=${(0, _live.live)(this.endCommit)}
            @input=${(e)=>this.endCommit = e.target.value}
          ></input>
          <h3>Tracing arguments</h3>
          <p>Learn more about
            <a href="${this.traceHelpLink}" target="_blank">
              filter strings<md-icon id="icon">open_in_new</md-icon>
            </a>
          <p>
          <input
            id="trace-args"
            type="text"
            .value=${(0, _live.live)(this.traceArgs)}
            @input=${(e)=>this.traceArgs = e.target.value}
          ></input>
          <div class=footer>
            <button
              id="pinpoint-try-job-dialog-submit"
              type="Submit"
              .disabled=${this._tryJobTask.status === _task.TaskStatus.PENDING}
            >Generate</button>
            <spinner-sk
              id="dialog-spinner"
              .active=${this._tryJobTask.status === _task.TaskStatus.PENDING}
            ></spinner-sk>
            ${this._renderTaskStatus()}
          </div>
        </form>
      </dialog>
    `;
    }
    _renderTaskStatus() {
        return this._tryJobTask.render({
            pending: ()=>(0, _lit.html)``,
            complete: (json)=>{
                if (!json) return (0, _lit.html)``;
                const url = json.jobUrl;
                return (0, _lit.html)`<a href="${url}" target="_blank">
          Pinpoint Job Created<md-icon id="icon">open_in_new</md-icon>
        </a>`;
            },
            error: (e)=>{
                console.error('Task error:', e);
                (0, _errorMessage.errorMessage)(e);
                return (0, _lit.html)``;
            },
            initial: ()=>(0, _lit.html)``
        });
    }
    constructor(...args){
        super(...args);
        this.jobUrl = '';
        this.testPath = '';
        this.baseCommit = '';
        this.endCommit = '';
        this.configuration = '';
        this.benchmark = '';
        this.traceArgs = 'toplevel,toplevel.flow,disabled-by-default-toplevel.flow';
        this.traceHelpLink = 'https://source.chromium.org/chromium/chromium/src/+/main:base/trace_event/trace_config.h;' + 'l=167;drc=04e98bf9e13012fda48001096174a4500ff27866;bpv=1';
        this.story = '';
        this.user = '';
        this._request = null;
        this._tryJobTask = new _task.Task(this, {
            task: async ([req], { signal })=>{
                if (!req) {
                    return null;
                }
                const response = await fetch('/_/try/', {
                    method: 'POST',
                    body: JSON.stringify(req),
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    signal
                });
                const json = await (0, _jsonOrThrow.jsonOrThrow)(response);
                return json;
            },
            args: ()=>[
                    this._request
                ]
        });
    }
};
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "jobUrl", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "testPath", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "baseCommit", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "endCommit", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "configuration", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "benchmark", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "traceArgs", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "traceHelpLink", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "story", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], PinpointTryJobDialogSk.prototype, "user", void 0);
_ts_decorate([
    (0, _decorators.query)('#pinpoint-try-job-dialog'),
    _ts_metadata("design:type", typeof HTMLDialogElement === "undefined" ? Object : HTMLDialogElement)
], PinpointTryJobDialogSk.prototype, "dialog", void 0);
_ts_decorate([
    (0, _decorators.query)('#pinpoint-try-job-form'),
    _ts_metadata("design:type", typeof HTMLFormElement === "undefined" ? Object : HTMLFormElement)
], PinpointTryJobDialogSk.prototype, "form", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Object)
], PinpointTryJobDialogSk.prototype, "_request", void 0);
PinpointTryJobDialogSk = _ts_decorate([
    (0, _decorators.customElement)('pinpoint-try-job-dialog-sk')
], PinpointTryJobDialogSk);
